import { useEffect, useRef, useState } from "react";
import { ChevronDown, Menu, X, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import banetechLogo from "@/assets/banetech-logo.webp";
import { products } from "@/data/products";

const links = [
  { href: "/#how", label: "How it works" },
  { href: "/#why", label: "Why Bane Tech" },
  { href: "/#products", label: "Products", hasMenu: true },
  { href: "/#about", label: "About" },
  { href: "/#faq", label: "FAQ" },
  { href: "/resources/", label: "Resources" },
  { href: "/#contact", label: "Contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileProducts, setMobileProducts] = useState(false);
  const closeTimer = useRef<number | undefined>(undefined);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setMenuOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const show = () => {
    window.clearTimeout(closeTimer.current);
    setMenuOpen(true);
  };
  const hide = () => {
    closeTimer.current = window.setTimeout(() => setMenuOpen(false), 120);
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled || menuOpen ? "bg-background/80 backdrop-blur-xl border-b border-border" : "bg-transparent"
      }`}
    >
      <div className="container flex h-16 items-center justify-between">
        <a href="/" className="flex items-center gap-2 font-display font-bold text-lg">
          <img src={banetechLogo} alt="Bane Tech" width={133} height={32} className="h-8 w-auto" />
        </a>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) =>
            l.hasMenu ? (
              <div key={l.href} className="relative" onMouseEnter={show} onMouseLeave={hide}>
                <button
                  type="button"
                  aria-expanded={menuOpen}
                  aria-haspopup="true"
                  onClick={() => setMenuOpen((v) => !v)}
                  className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  {l.label}
                  <ChevronDown className={`h-3.5 w-3.5 transition-transform ${menuOpen ? "rotate-180" : ""}`} />
                </button>
                {menuOpen && (
                  <div className="absolute left-1/2 top-full -translate-x-1/2 pt-4">
                    <div className="w-[580px] rounded-xl border border-border bg-popover text-popover-foreground shadow-xl overflow-hidden animate-in fade-in-0 zoom-in-95">
                      <ul className="grid grid-cols-2 gap-1 p-3">
                        {products.map((p) => (
                          <li key={p.slug}>
                            <a
                              href={`/products/${p.slug}/`}
                              onClick={() => setMenuOpen(false)}
                              className="flex items-start gap-3 rounded-lg p-3 hover:bg-muted transition-colors"
                            >
                              <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br ${p.accent}`}>
                                <img src={p.icon} alt="" width={20} height={20} className="h-5 w-5" />
                              </span>
                              <span className="min-w-0">
                                <span className="block text-sm font-semibold text-foreground">{p.name}</span>
                                <span className="block text-xs leading-snug text-muted-foreground">{p.tagline}</span>
                              </span>
                            </a>
                          </li>
                        ))}
                      </ul>
                      <a
                        href="/#products"
                        onClick={() => setMenuOpen(false)}
                        className="flex items-center justify-between border-t border-border bg-muted/50 px-6 py-3 text-sm text-muted-foreground hover:text-foreground transition-colors"
                      >
                        <span>Overview: explore all {products.length} products</span>
                        <ArrowRight className="h-4 w-4" />
                      </a>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <a key={l.href} href={l.href} className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                {l.label}
              </a>
            ),
          )}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <Button variant="hero" size="sm" asChild>
            <a href="/#contact">Request Demo</a>
          </Button>
        </div>

        <button
          className="md:hidden p-2 text-foreground"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-border bg-background/95 backdrop-blur-xl max-h-[calc(100vh-4rem)] overflow-y-auto">
          <div className="container py-4 flex flex-col gap-3">
            {links.map((l) =>
              l.hasMenu ? (
                <div key={l.href}>
                  <button
                    type="button"
                    aria-expanded={mobileProducts}
                    onClick={() => setMobileProducts((v) => !v)}
                    className="flex w-full items-center justify-between text-sm py-2 text-muted-foreground hover:text-foreground"
                  >
                    {l.label}
                    <ChevronDown className={`h-4 w-4 transition-transform ${mobileProducts ? "rotate-180" : ""}`} />
                  </button>
                  {mobileProducts && (
                    <div className="mt-1 flex flex-col gap-1 border-l border-border pl-4">
                      {products.map((p) => (
                        <a
                          key={p.slug}
                          href={`/products/${p.slug}/`}
                          onClick={() => setOpen(false)}
                          className="py-1.5"
                        >
                          <span className="block text-sm text-foreground">{p.name}</span>
                          <span className="block text-xs text-muted-foreground">{p.tagline}</span>
                        </a>
                      ))}
                      <a href="/#products" onClick={() => setOpen(false)} className="py-1.5 text-sm text-muted-foreground hover:text-foreground">
                        All products
                      </a>
                    </div>
                  )}
                </div>
              ) : (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="text-sm py-2 text-muted-foreground hover:text-foreground"
                >
                  {l.label}
                </a>
              ),
            )}
            <Button variant="hero" size="sm" asChild className="mt-2">
              <a href="/#contact" onClick={() => setOpen(false)}>Request Demo</a>
            </Button>
          </div>
        </div>
      )}
    </header>
  );
};
